import { ExternalLink, Link2 } from 'lucide-react-native';
import { useEffect, useState } from 'react';
import { Image, Platform, Pressable, StyleSheet, Text, View } from 'react-native';

import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';
import { webTransition } from '@/lib/hover-style';
import { fetchLinkPreview, type LinkPreview } from '@/lib/link-preview';

interface Props {
  url: string;
  /** Compact = preview row layout (no big image), suitable for a list
   *  card on /notes. Full = image-on-top layout, suitable for a hero
   *  treatment inside the note editor or a feature row. */
  variant?: 'compact' | 'full';
}

/**
 * Render an OG-style preview card for a single URL. Fetches via
 * `fetchLinkPreview` (Microlink-backed, cached in localStorage) and
 * shows a placeholder while loading. Tap → opens the URL in a new tab
 * on web; on native it's display-only (we don't auto-open external
 * links without user intent).
 */
export function LinkPreviewCard({ url, variant = 'compact' }: Props) {
  const [preview, setPreview] = useState<LinkPreview | null | 'loading'>('loading');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const p = await fetchLinkPreview(url);
      if (!cancelled) setPreview(p);
    })();
    return () => {
      cancelled = true;
    };
  }, [url]);

  const openUrl = () => {
    if (Platform.OS !== 'web') return;
    // eslint-disable-next-line no-undef
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  if (preview === 'loading') {
    return (
      <View style={[styles.wrap, variant === 'compact' ? styles.wrapCompact : styles.wrapFull]}>
        <View style={styles.skeletonImage} />
        <View style={styles.bodyCol}>
          <View style={[styles.skeletonLine, { width: '80%' }]} />
          <View style={[styles.skeletonLine, { width: '60%', marginTop: 4 }]} />
        </View>
      </View>
    );
  }

  // Fallback when fetch failed — still show the URL as a clickable
  // pill so the note never displays just a raw text URL.
  if (!preview) {
    const host = (() => {
      try {
        return new URL(url).hostname.replace(/^www\./, '');
      } catch {
        return url;
      }
    })();
    return (
      <Pressable
        onPress={openUrl}
        accessibilityLabel={`Open ${host}`}
        style={({ pressed, hovered }) => [
          styles.fallback,
          webTransition,
          hovered && styles.fallbackHover,
          pressed && { opacity: 0.85 },
        ]}>
        <Link2 size={13} color={Palette.forest} strokeWidth={2.2} />
        <Text style={styles.fallbackHost} numberOfLines={1}>{host}</Text>
        <ExternalLink size={11} color={Palette.inkFaint} strokeWidth={2} />
      </Pressable>
    );
  }

  const title = preview.title || preview.host || preview.url;
  const desc = preview.description;
  const host = preview.host || '';

  return (
    <Pressable
      onPress={openUrl}
      accessibilityLabel={`Open ${title}`}
      style={({ pressed, hovered }) => [
        styles.wrap,
        variant === 'compact' ? styles.wrapCompact : styles.wrapFull,
        webTransition,
        hovered && styles.wrapHover,
        pressed && { opacity: 0.9 },
      ]}>
      {preview.image ? (
        <Image
          source={{ uri: preview.image }}
          accessibilityLabel={title}
          resizeMode="cover"
          style={variant === 'compact' ? styles.imageCompact : styles.imageFull}
        />
      ) : (
        <View style={[
          variant === 'compact' ? styles.imageCompact : styles.imageFull,
          styles.imageFallback,
        ]}>
          <Link2 size={20} color={Palette.inkFaint} strokeWidth={1.7} />
        </View>
      )}
      <View style={styles.bodyCol}>
        <Text style={styles.title} numberOfLines={2}>
          {title}
        </Text>
        {desc ? (
          <Text style={styles.desc} numberOfLines={variant === 'full' ? 3 : 2}>
            {desc}
          </Text>
        ) : null}
        <View style={styles.foot}>
          {host ? <Text style={styles.host}>{host}</Text> : null}
          <ExternalLink size={10} color={Palette.inkFaint} strokeWidth={2} />
        </View>
      </View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  wrap: {
    backgroundColor: Palette.card,
    borderWidth: 1,
    borderColor: Palette.line,
    borderRadius: Radius.lg,
    overflow: 'hidden',
  },
  // Compact variant — image on the left, body on the right. Used as
  // the body of a NoteCard when the note is just a URL.
  wrapCompact: {
    flexDirection: 'row',
    gap: Spacing.three,
    alignItems: 'stretch',
    padding: 0,
  },
  // Full variant — image hero on top, body below.
  wrapFull: {
    flexDirection: 'column',
  },
  wrapHover: {
    borderColor: Palette.forestLight,
    backgroundColor: '#FBF6EA',
  },
  imageCompact: {
    width: 96,
    height: 96,
    backgroundColor: Palette.sandDeep,
  },
  imageFull: {
    width: '100%',
    height: 160,
    backgroundColor: Palette.sandDeep,
  },
  imageFallback: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  bodyCol: {
    flex: 1,
    minWidth: 0,
    paddingVertical: Spacing.three,
    paddingHorizontal: Spacing.three,
    gap: 4,
  },
  title: {
    fontFamily: Fonts.sansBold,
    fontSize: 13.5,
    color: Palette.ink,
    letterSpacing: -0.05,
    lineHeight: 17,
  },
  desc: {
    fontFamily: Fonts.sans,
    fontSize: 12.5,
    color: Palette.inkSoft,
    lineHeight: 15,
  },
  foot: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    marginTop: 'auto',
    paddingTop: 4,
  },
  host: {
    fontFamily: Fonts.sansBold,
    fontSize: 11,
    color: Palette.honeyDeep,
    letterSpacing: 0.3,
    textTransform: 'uppercase',
  },
  skeletonImage: {
    width: 96,
    height: 96,
    backgroundColor: Palette.sandDeep,
  },
  skeletonLine: {
    height: 10,
    backgroundColor: Palette.sandDeep,
    borderRadius: 4,
  },
  fallback: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: Spacing.three,
    paddingVertical: 8,
    borderRadius: Radius.md,
    backgroundColor: Palette.sand,
    borderWidth: 1,
    borderColor: Palette.line,
    alignSelf: 'flex-start',
  },
  fallbackHover: {
    backgroundColor: '#EAF0EA',
    borderColor: Palette.forestLight,
  },
  fallbackHost: {
    fontFamily: Fonts.sansBold,
    fontSize: 13,
    color: Palette.forest,
    letterSpacing: 0.2,
  },
});
